import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddStudentGroupDialog from '../../Forms/PrivateFormSettings/AddStudentGroupDialog';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import GroupsHelpText from '../../Forms/PrivateFormSettings/GroupsHelpText';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import FieldsTab from '../../Profiles/DownloadReportPopup/FieldsTab';
import {STUDENT_FIELDS_MAPPING, EMPLOYEE_FIELDS_MAPPING} from '../../Profiles/const/ReportFieldsMapping';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {
    downloadEmployeesCsvReport,
    downloadEmployeesXlsxReport,
    downloadMedicalEventsCsvReport,
    downloadMedicalEventsXlsxReport,
    downloadMedicineDispenseReport,
    downloadMedicineDispenseXlsxReport,
    downloadStudentsCsvReport,
    downloadStudentsXlsxReport
} from '../../../../api/services/filesService';
import {filterPayload} from '../ReportCards/ReportFilterDialog';
import {canRunMedicalEventsReport, canRunMedicineAtSchoolReport} from '../../../../utility/reportFieldAccess';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import useNotification from '../../../../utility/hooks/useNotification';
import {recordReportRun, saveReport} from '../../../../api/services/savedReportsService';
import useUser from '../../../../utility/hooks/useUser';
import {ROLES} from '../../../../constants/roles';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {firstRowsOfCsv, isEmptyReportMessage, reportPayload} from './createReportRules';

// AD-2699 (Maria, 15 October 2026): the Create Report box is an easier way to reach the same reports. It asks the
// same questions as the 'Report Generation' box, one at a time, in the same style as the Create Form box, and sends
// exactly the same request, so the file is the same. 'Preview Report' is the only new step.

// Maria, 15 October 2026: 'Student report' and 'Employee report' on the first page save a page.
const REGULAR_STEPS = ['kind', 'who', 'details', 'preview', 'download', 'save', 'name', 'share'];
// Running a saved report: straight to the preview, then the download.
const RUN_STEPS = ['preview', 'download'];
// The medical reports ask what their cards asked: the dates, then who (the cards' 'Filters').
const MEDICAL_STEPS = ['kind', 'dates', 'who', 'preview', 'download'];

const QUESTIONS = {
    kind: ['Select a Report Type', ''],
    dates: ['Which dates should the report cover?', 'Leave a date empty to include everything before or after the other date.'],
    who: ['Who should the report include?', ''],
    details: ['Select the fields you want to include', ''],
    preview: ['Preview Report', ''],
    download: ['Download Report', 'Choose the type of file to download.'],
    save: ['Would you like to save this report to run again?', ''],
    name: ['What would you like to call this report?', ''],
    share: ['Who can use this report?', ''],
};

const KIND_TIPS = {
    // Maria, 15 October 2026: the words of the old report card.
    student: 'Detailed learner and school data.',
    employee: 'Detailed staff data.',
    medicine: 'Every dose of medicine given at school, with who gave it and when.',
    events: 'Every medical event recorded on a child\'s profile, with its date and follow-up date.',
};

const MEDICAL_DOWNLOADS = {
    medicine: {csv: downloadMedicineDispenseReport, xlsx: downloadMedicineDispenseXlsxReport, unit: ['dose', 'doses']},
    events: {csv: downloadMedicalEventsCsvReport, xlsx: downloadMedicalEventsXlsxReport, unit: ['event', 'events']},
};

// Maria, 15 October 2026: a pop-up is just the text, with no box, border or shadow around it.
export const PLAIN_TOOLTIP = {
    tooltip: {
        sx: {
            bgcolor: 'transparent',
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 400,
            border: 'none',
            boxShadow: 'none',
            p: 0,
            maxWidth: 480,
            // longer text breaks into lines of even length
            textWrap: 'balance'
        }
    }
};

// Maria, 15 October 2026: two choices only. Whoever runs a report still sees only what their role can view.
const SHARE_CHOICES = [
    {value: 'me', text: 'Save this report for me'},
    {value: 'everyone', text: 'Save this report for anyone with permission to view it'},
];

const emptySettings = () => ({
    groupFilters: [],
    studentFilters: [],
    applicationFilters: [],
    enquiryFilters: [],
    employeeFilters: []
});

const ChoiceTip = ({tip}) => (
    <Tooltip title={tip} placement="right" componentsProps={PLAIN_TOOLTIP}>
        <InfoOutlinedIcon
            fontSize="small"
            data-testid="report-choice-tip"
            sx={{color: 'text.secondary', ml: 0.75, verticalAlign: 'middle', cursor: 'help'}}
            onClick={(event) => event.preventDefault()}
        />
    </Tooltip>
);

ChoiceTip.propTypes = {tip: PropTypes.string};

const Choice = ({value, text, tip}) => (
    <FormControlLabel
        value={value}
        control={<Radio size="small" sx={{py: 0.5}}/>}
        sx={{alignItems: 'flex-start', m: 0, minHeight: 32, '& .MuiFormControlLabel-label': {pt: '5px'}}}
        label={<Box component="span" sx={{display: 'block', lineHeight: 1.3}}>{text}<ChoiceTip tip={tip}/></Box>}
        data-testid={`report-option-${value}`}
    />
);

Choice.propTypes = {value: PropTypes.string, text: PropTypes.string, tip: PropTypes.string};

const CreateReportBox = ({open, groupFilters, onClose, savedReport = null, mode = 'create', onSaved}) => {
    const {user} = useUser();
    const isSchoolAdmin = user.hasRole(ROLES.SCHOOL_ADMIN);
    const {data: schoolInfo} = useGetCurrentSchoolQuery();
    const downloadReport = useFileDownloader();
    const {showErrorNotification} = useNotification();

    const [step, setStep] = useState('kind');
    const [kind, setKind] = useState('');
    const [memberType, setMemberType] = useState(GROUP_MEMBERS_TYPE.students);
    const [settings, setSettings] = useState(emptySettings);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [excludedFields, setExcludedFields] = useState(
        STUDENT_FIELDS_MAPPING.filter(field => !field.readonly).map(field => field.value)
    );
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [wantsSave, setWantsSave] = useState('');
    const [reportName, setReportName] = useState('');
    const [sharedWith, setSharedWith] = useState('me');
    const [addGroupOpen, setAddGroupOpen] = useState(false);

    const isMedical = kind === 'medicine' || kind === 'events';
    const STEPS = mode === 'run' ? RUN_STEPS : (isMedical ? MEDICAL_STEPS : REGULAR_STEPS);
    const isEmployeeReport = !isMedical && memberType === GROUP_MEMBERS_TYPE.employee;
    const stepNumber = STEPS.indexOf(step) + 1;
    const [question, line] = QUESTIONS[step];

    // The same group list as the 'Report Generation' box, for students or for employees.
    const groupModules = useMemo(() => {
        if (!groupFilters?.length) return [];
        if (isEmployeeReport) {
            return groupFilters
                .filter(group => group.name === 'EMPLOYEES' ||
                    group.name?.toLowerCase().includes('school group') ||
                    group.name?.toLowerCase().includes('my group'))
                .map(({filters, ...rest}) => ({
                    filters: filters.filter(filter => filter.membersType === GROUP_MEMBERS_TYPE.employee),
                    ...rest
                }));
        }
        // Maria, 15 October 2026: enquiries only for a school that uses SIMS Enquiries.
        const usesEnquiries = !(schoolInfo == null || schoolInfo.disableEnquiriesSync);
        return groupFilters
            .filter(module => module.membersType !== GROUP_MEMBERS_TYPE.employee)
            .filter(module => usesEnquiries || !(module.name || '').toUpperCase().includes('ENQUIRY'))
            .map(({filters, ...rest}) => ({
                filters: filters.filter(filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee),
                ...rest
            }));
    }, [groupFilters, isEmployeeReport, schoolInfo]);
    const included = settings.groupFilters.filter(filter => !filter.excluded);
    const hasIndividuals = isEmployeeReport
        ? settings.employeeFilters.length > 0
        : settings.studentFilters.length + settings.applicationFilters.length + settings.enquiryFilters.length > 0;
    // As in the 'Report Generation' box, the report needs a group included or a person chosen.
    const hasSomeone = included.length > 0 || hasIndividuals;

    // Exactly the request today's box or card sends for the same choices.
    const payload = isMedical
        ? {from: from || null, to: to || null, ...filterPayload(settings, strictRecipientsFiltering)}
        : reportPayload({memberType, settings, excludedFields, strictRecipientsFiltering});
    const csvDownload = isMedical ? MEDICAL_DOWNLOADS[kind].csv : (isEmployeeReport ? downloadEmployeesCsvReport : downloadStudentsCsvReport);
    const xlsxDownload = isMedical ? MEDICAL_DOWNLOADS[kind].xlsx : (isEmployeeReport ? downloadEmployeesXlsxReport : downloadStudentsXlsxReport);
    const unit = isMedical ? MEDICAL_DOWNLOADS[kind].unit : ['person', 'people'];

    const reset = () => {
        setStep('kind');
        setKind('');
        setMemberType(GROUP_MEMBERS_TYPE.students);
        setSettings(emptySettings());
        setStrictRecipientsFiltering(false);
        setExcludedFields(STUDENT_FIELDS_MAPPING.filter(field => !field.readonly).map(field => field.value));
        setPreview(null);
        setFrom('');
        setTo('');
        setWantsSave('');
        setReportName('');
        setSharedWith('me');
    };

    // Editing or running a saved report: the box opens with every choice it was saved with.
    useEffect(() => {
        if (!open || !savedReport) return;
        const saved = JSON.parse(savedReport.settings || '{}');
        setKind(saved.kind || (savedReport.memberType === 'employee' ? 'employee' : 'student'));
        setMemberType(savedReport.memberType === 'employee' ? GROUP_MEMBERS_TYPE.employee : GROUP_MEMBERS_TYPE.students);
        setSettings({...emptySettings(), ...(saved.settings || {})});
        setExcludedFields(saved.excludedFields || []);
        setStrictRecipientsFiltering(Boolean(saved.strictRecipientsFiltering));
        setWantsSave('yes');
        setReportName(savedReport.name);
        setSharedWith(savedReport.sharedWith === 'me' ? 'me' : 'everyone');
        setPreview(null);
        setStep(mode === 'run' ? 'preview' : 'who');
        if (mode === 'run') recordReportRun(savedReport.id).catch(() => {});
    }, [open, savedReport, mode]);

    const close = () => {
        reset();
        onClose();
    };

    const chooseMemberType = (next) => {
        setMemberType(next);
        const fields = next === GROUP_MEMBERS_TYPE.employee ? EMPLOYEE_FIELDS_MAPPING : STUDENT_FIELDS_MAPPING;
        setExcludedFields(fields.filter(field => !field.readonly).map(field => field.value));
        setSettings(emptySettings());
        setStrictRecipientsFiltering(false);
    };

    // As on the form box's 'Add Recipients' page: a group added again replaces the earlier choice.
    const addGroup = (filter) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(path => path.name).join(' - '),
            excluded: filter.excluded
        };
        setSettings(prev => {
            const known = prev.groupFilters.some(group => group.id === newFilter.id);
            const groups = known
                ? prev.groupFilters.map(group => (group.id === newFilter.id ? newFilter : group))
                : [...prev.groupFilters, newFilter];
            return {...prev, groupFilters: groups};
        });
    };

    const removeGroup = (id) => setSettings(prev => ({...prev, groupFilters: prev.groupFilters.filter(filter => filter.id !== id)}));

    const loadPreview = async () => {
        setBusy(true);
        setPreview(null);
        try {
            const response = await csvDownload(payload);
            if (response.error) {
                const message = response.error.customMessage ?? '';
                // Maria, 15 October 2026: an empty report is not an error. The server says so in its own words
                // ("None of the chosen students were given medicine...", "There were no medical events...").
                setPreview(isEmptyReportMessage(message)
                    ? {empty: true}
                    : {error: message || 'The report could not be previewed.'});
                return;
            }
            const text = await new Blob([response.blob]).text();
            const rows = firstRowsOfCsv(text, 20);
            setPreview(rows.count === 0 ? {empty: true} : rows);
        } finally {
            setBusy(false);
        }
    };

    const next = () => {
        if (step === 'save' && wantsSave === 'no') {
            close();
            return;
        }
        setStep(STEPS[stepNumber]);
    };

    useEffect(() => {
        if (open && step === 'preview') loadPreview();
    }, [open, step]);

    const back = () => (stepNumber <= 1 ? close() : setStep(STEPS[stepNumber - 2]));

    const sharingComplete = sharedWith === 'me' || sharedWith === 'everyone';

    const save = async () => {
        setBusy(true);
        try {
            const saved = await saveReport({
                id: savedReport?.id ?? null,
                name: reportName,
                memberType: isEmployeeReport ? 'employee' : 'students',
                settings: JSON.stringify({kind, settings, excludedFields, strictRecipientsFiltering}),
                sharedWith,
                sharedRoles: [],
                sharedGroupFilterIds: [],
                sharedEmployeeIds: [],
                sharedNames: '{}',
            });
            onSaved?.(saved);
            close();
        } catch (error) {
            showErrorNotification(error?.response?.data?.message ?? 'The report could not be saved.');
        } finally {
            setBusy(false);
        }
    };

    const canGoOn =
        (step === 'kind' && Boolean(kind)) ||
        step === 'dates' ||
        // As today: a regular report needs someone chosen; a medical report with nobody chosen covers every child.
        ((step === 'who' || step === 'details') && (isMedical || hasSomeone)) ||
        (step === 'preview' && Boolean(preview) && !preview.error && !preview.empty) ||
        step === 'download' ||
        (step === 'save' && Boolean(wantsSave)) ||
        (step === 'name' && reportName.trim().length > 0);

    const download = async (asCsv) => {
        setBusy(true);
        try {
            await downloadReport(asCsv ? csvDownload : xlsxDownload, payload);
        } finally {
            setBusy(false);
        }
    };

    const nameSearch = (label, key, pupilType) => (
        <PaginatedAutocomplete
            textFieldProps={{size: 'small', label, placeholder: `Search ${label.toLowerCase()}`}}
            fetchFn={pupilType ? useLazyGetStudentFiltersQuery : useLazyGetAllEmployeeProfilesQuery}
            fetchFnProps={pupilType ? {queryParams: {pupilType}} : undefined}
            getOptionLabel={(option) => option.label || `${option.preferredForename} ${option.preferredSurname}`}
            value={settings[key] || []}
            onChange={(e, newValue) => setSettings(prev => ({...prev, [key]: newValue}))}
        />
    );

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={close}
            data-testid="create-report-dialog"
            PaperProps={{sx: {borderRadius: 3, height: 'calc(100% - 64px)', ...(['who', 'details', 'preview'].includes(step) ? {maxWidth: 760} : {})}}}
        >
            <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                        Report Setup
                    </Typography>
                    <IconButton size="small" onClick={close} aria-label="Close">
                        <CancelIcon fontSize="small"/>
                    </IconButton>
                </Stack>
                <Typography variant="h5" sx={{fontWeight: 600}} data-testid="create-report-question">{question}</Typography>
                {line && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}} data-testid="create-report-line">{line}</Typography>}
                <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                    {STEPS.map((name, index) => (
                        <Box key={name} sx={{flex: 1, height: 4, borderRadius: 2, backgroundColor: index < stepNumber ? '#186BBE' : '#e3e8ef'}}/>
                    ))}
                </Stack>
            </Box>

            {/* The same padding as the Create Form box: the busy pages (its 'Add Recipients' and 'Restrictions') sit
                closer to the edges and the top; the choice pages keep the wider margins. */}
            <DialogContent sx={{
                px: ['who', 'details', 'preview'].includes(step) ? 3 : 4,
                pt: step === 'who' ? 0.5 : (['details', 'preview', 'dates', 'download'].includes(step) ? 2 : 3),
                pb: ['who', 'details'].includes(step) ? 1 : 3
            }}>
                {step === 'kind' && (
                    <RadioGroup
                        value={kind}
                        onChange={(e) => {
                            const next = e.target.value;
                            setKind(next);
                            if (next === 'student' || next === 'employee') {
                                chooseMemberType(next === 'employee' ? GROUP_MEMBERS_TYPE.employee : GROUP_MEMBERS_TYPE.students);
                            }
                        }}
                        sx={{gap: 0.5}}
                        data-testid="report-kind"
                    >
                        <Choice value="student" text="Student report" tip={KIND_TIPS.student}/>
                        {/* AD-2698: only a School Admin can open employee profiles, so only a School Admin can report on employees. */}
                        {isSchoolAdmin && <Choice value="employee" text="Employee report" tip={KIND_TIPS.employee}/>}
                        {/* AD-2698: each medical report only for a role that can view its section in Manage Access. */}
                        {canRunMedicineAtSchoolReport(user.permissions) && (
                            <Choice value="medicine" text="Medicine at School report" tip={KIND_TIPS.medicine}/>
                        )}
                        {canRunMedicalEventsReport(user.permissions) && (
                            <Choice value="events" text="Medical Events report" tip={KIND_TIPS.events}/>
                        )}
                    </RadioGroup>
                )}

                {step === 'dates' && (
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2} data-testid="report-dates">
                        <TextField
                            label="From"
                            type="date"
                            size="small"
                            InputLabelProps={{shrink: true}}
                            data-testid="report-from"
                            value={from}
                            onChange={(event) => setFrom(event.target.value)}
                        />
                        <TextField
                            label="To"
                            type="date"
                            size="small"
                            InputLabelProps={{shrink: true}}
                            data-testid="report-to"
                            value={to}
                            onChange={(event) => setTo(event.target.value)}
                        />
                    </Stack>
                )}

                {step === 'who' && (
                    <Box data-testid="report-who">
                        <GroupsHelpText forReport who={isEmployeeReport ? 'employees' : 'children'} sx={{mb: 0.5}}/>
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 0.5}}>
                            <Button
                                variant="outlined"
                                size="small"
                                data-testid="report-add-group"
                                startIcon={<AddOutlinedIcon/>}
                                onClick={() => setAddGroupOpen(true)}
                                sx={{minWidth: 160}}
                            >
                                Add Group
                            </Button>
                        </Box>
                        {/* Maria, 15 October 2026: a plain table, so the headings line up with their columns, shown once a group is added. */}
                        {settings.groupFilters.length > 0 && (
                            <TableContainer sx={{border: '1px solid #e3e8ef', borderRadius: 1}} data-testid="report-groups">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 600}}>Group Name</TableCell>
                                            <TableCell sx={{fontWeight: 600, width: 120}}>Status</TableCell>
                                            <TableCell sx={{fontWeight: 600, width: 80}} align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {settings.groupFilters.map(group => (
                                            <TableRow key={group.id} sx={{'&:last-child td': {borderBottom: 0}}}>
                                                <TableCell>{group.displayName}</TableCell>
                                                <TableCell sx={{color: group.excluded ? 'error.main' : 'success.main'}}>
                                                    {group.excluded ? 'Excluded' : 'Included'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton onClick={() => removeGroup(group.id)} size="small" aria-label="Remove group">
                                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {included.length > 1 && (
                            <FormControlLabel
                                // Maria, 15 October 2026: indented a little, with smaller words.
                                sx={{ml: 1}}
                                control={(
                                    <Checkbox
                                        checked={strictRecipientsFiltering}
                                        onChange={(e) => setStrictRecipientsFiltering(e.target.checked)}
                                    />
                                )}
                                label={(
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14}}>
                                        Must appear in all selected groups
                                        <Tooltip title="Applies to included groups only" placement="right" componentsProps={{tooltip: {sx: {bgcolor: 'white', color: '#186BBE', fontSize: 14, fontWeight: 400, boxShadow: 'none', border: '1px solid #186BBE', borderRadius: 1, px: 1, py: 0.5}}}}>
                                            <InfoOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                        </Tooltip>
                                    </Box>
                                )}
                            />
                        )}
                        <Typography variant="subtitle1" sx={{fontWeight: 600, mt: 0.5, mb: 0.5}}>
                            {isEmployeeReport ? 'Add employees individually' : 'Add students or applicants individually'}
                        </Typography>
                        <Stack spacing={1}>
                            {isEmployeeReport
                                ? nameSearch('Employees', 'employeeFilters')
                                : (
                                    <>
                                        {nameSearch('Applicants', 'applicationFilters', 'applicant')}
                                        {nameSearch('Students', 'studentFilters', 'student')}
                                        {!(schoolInfo == null || schoolInfo.disableEnquiriesSync) &&
                                            nameSearch('Enquiries', 'enquiryFilters', 'enquiry')}
                                    </>
                                )}
                        </Stack>
                        <AddStudentGroupDialog
                            open={addGroupOpen}
                            groupFilters={groupModules}
                            onClose={() => setAddGroupOpen(false)}
                            onChange={addGroup}
                            boxWidth={760}
                        />
                    </Box>
                )}

                {step === 'details' && (
                    <FieldsTab
                        excludedFields={excludedFields}
                        handleToggleField={(field) => setExcludedFields(prev =>
                            (prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]))}
                        setExcludedFields={setExcludedFields}
                        memberType={memberType}
                    />
                )}

                {step === 'preview' && (
                    <Box data-testid="report-preview">
                        {busy && <CircularProgress size={28}/>}
                        {preview?.error && <Typography color="error">{preview.error}</Typography>}
                        {preview?.empty && (
                            <Typography data-testid="report-preview-empty">No students match the criteria for this report.</Typography>
                        )}
                        {preview && !preview.error && !preview.empty && (
                            <>
                                <Typography variant="body2" sx={{mb: 1.5}} data-testid="report-preview-count">
                                    {`The report ${isMedical ? 'has' : 'covers'} ${preview.count} ${preview.count === 1 ? unit[0] : unit[1]}.`}
                                    {preview.count > preview.rows.length ? ` The first ${preview.rows.length} are shown here.` : ''}
                                </Typography>
                                <TableContainer sx={{border: '1px solid #e3e8ef', borderRadius: 1, maxHeight: 420}}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {preview.header.map(cell => <TableCell key={cell} sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>{cell}</TableCell>)}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {preview.rows.map((row, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <TableRow key={index}>
                                                    {row.map((cell, cellIndex) => (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <TableCell key={cellIndex} sx={{whiteSpace: 'nowrap'}}>{cell}</TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>
                )}

                {step === 'download' && (
                    <Stack direction="row" spacing={2} data-testid="report-download">
                        <Button variant="contained" disabled={busy} onClick={() => download(false)} data-testid="report-download-xlsx">
                            Download as Excel
                        </Button>
                        <Button variant="outlined" disabled={busy} onClick={() => download(true)} data-testid="report-download-csv">
                            Download as CSV
                        </Button>
                    </Stack>
                )}

                {step === 'save' && (
                    <RadioGroup value={wantsSave} onChange={(e) => setWantsSave(e.target.value)} sx={{gap: 0.5}} data-testid="report-save">
                        <Choice value="yes" text="Yes" tip="The report is kept on the Reports page, with its choices, to run again."/>
                        <Choice value="no" text="No, I have finished" tip="The report is not kept."/>
                    </RadioGroup>
                )}

                {step === 'name' && (
                    <TextField
                        fullWidth
                        size="small"
                        label="Report name"
                        value={reportName}
                        inputProps={{maxLength: 200}}
                        onChange={(e) => setReportName(e.target.value)}
                        data-testid="report-name"
                    />
                )}

                {step === 'share' && (
                    <Stack spacing={1.5} data-testid="report-share">
                        <RadioGroup value={sharedWith} onChange={(e) => setSharedWith(e.target.value)} sx={{gap: 0.5}}>
                            {SHARE_CHOICES.map(choice => (
                                <FormControlLabel
                                    key={choice.value}
                                    value={choice.value}
                                    control={<Radio size="small" sx={{py: 0.5}}/>}
                                    label={choice.text}
                                    data-testid={`report-share-${choice.value}`}
                                />
                            ))}
                        </RadioGroup>
                    </Stack>
                )}
            </DialogContent>

            <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between">
                    <Button data-testid="report-back" variant="outlined" onClick={back}>Back</Button>
                    {(step === 'download' && (isMedical || mode === 'run')) && (
                        <Button data-testid="report-finish" variant="contained" onClick={close}>Finish</Button>
                    )}
                    {step === 'share' && (
                        <Button
                            data-testid="report-save-button"
                            variant="contained"
                            onClick={save}
                            disabled={!sharingComplete || busy}
                            sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                        >
                            {savedReport ? 'Save changes' : 'Save report'}
                        </Button>
                    )}
                    {!(step === 'download' && (isMedical || mode === 'run')) && step !== 'share' && (
                            <Button
                                data-testid="report-next"
                                variant="contained"
                                onClick={next}
                                disabled={!canGoOn || busy}
                                sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                            >
                                {step === 'save' && wantsSave === 'no' ? 'Finish' : 'Next'}
                            </Button>
                        )}
                </Stack>
            </Box>
        </Dialog>
    );
};

CreateReportBox.propTypes = {
    open: PropTypes.bool,
    groupFilters: PropTypes.array,
    onClose: PropTypes.func,
    savedReport: PropTypes.object,
    mode: PropTypes.oneOf(['create', 'edit', 'run']),
    onSaved: PropTypes.func
};

export default CreateReportBox;
